import { BaseRecord } from '@refinedev/core';
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
export interface ExperienceUserStoreConfig {
  storageKey: string;
}

export interface ExperienceUserState extends BaseRecord {
  id?: string;
  error: null;
  activeExperienceUserId: number | null;
  aiGradingInProgressForEUIds: number[];
  studentNameFormat: 'first_name' | 'last_name';
  overallGradingFormVisible: boolean;
  sortedExperienceUserIds: any[];
  isOpenExperienceUserGradingModal: boolean;
  isOpenOverallGradingModal: boolean;
  activeExperienceUserExperienceId: number | null;
  isGradeSavingInProgress: boolean;
  annotations: any[];
  assignedReviewersFromStore: Record<number, number[]>;
}

interface ExperienceUserActions {
  setActiveExperienceUserId: (id: number | null) => void;
  setAIGradingInProgressForEUIds: (
    ids: number[],
    options?: { merge?: boolean; remove?: boolean }
  ) => void;
  setStudentNameFormat: (format: 'first_name' | 'last_name') => void;
  setOverallGradingFormVisible: (value: boolean) => void;
  setSortedExperienceUserIds: (ids: any[]) => void;
  setIsOpenExperienceUserGradingModal: (value: boolean) => void;
  setIsOpenOverallGradingModal: (value: boolean) => void;
  setActiveExperienceUserDetails: (payload: any) => void;
  reset: () => void;
  setIsGradeSavingInProgress: (value: boolean) => void;
  setAnnotations: (annotations: any[] | undefined, options?: { merge?: boolean }) => void;
  setAssignedReviewersToStore: (experienceUserId: number, reviewerUserIds: number[]) => void;
  clearAssignedReviewersFromStore: () => void;
}

type ExperienceUserStore = ExperienceUserState &
  ExperienceUserActions & { config: ExperienceUserStoreConfig };

export const experienceUserStoreConfig: ExperienceUserStoreConfig = {
  storageKey: 'experienceUserState',
};

const initialState: ExperienceUserState = {
  id: 'experienceUserStore',
  error: null,
  activeExperienceUserId: null,
  aiGradingInProgressForEUIds: [],
  studentNameFormat: 'first_name',
  overallGradingFormVisible: false,
  sortedExperienceUserIds: [],
  isOpenExperienceUserGradingModal: false,
  isOpenOverallGradingModal: false,
  experience_users: {},
  activeExperienceUserExperienceId: null,
  isGradeSavingInProgress: false,
  annotations: [],
  assignedReviewersFromStore: {},
};

export const useExperienceUserStore = create<ExperienceUserStore>()(
  immer((set, get) => ({
    ...initialState,
    config: experienceUserStoreConfig,
    setAIGradingInProgressForEUIds: (ids, options = { merge: true }) =>
      set((state) => {
        if (options.remove) {
          state.aiGradingInProgressForEUIds = state.aiGradingInProgressForEUIds.filter(
            (id) => !ids.includes(id)
          );
        } else if (options.merge) {
          state.aiGradingInProgressForEUIds = Array.from(
            new Set([...state.aiGradingInProgressForEUIds, ...ids])
          );
        } else {
          state.aiGradingInProgressForEUIds = ids;
        }
      }),
    setStudentNameFormat: (format: 'first_name' | 'last_name') =>
      set((state) => {
        state.studentNameFormat = format;
      }),
    setActiveExperienceUserId: (id: number | null) =>
      set((state) => {
        state.activeExperienceUserId = id;
      }),
    setActiveExperienceUserDetails: (payload: any) =>
      set((state) => {
        state.activeExperienceUserId = payload.id;
        state.activeExperienceUserExperienceId = payload.experience_id;
      }),
    setOverallGradingFormVisible: (value: boolean) =>
      set((state) => {
        state.overallGradingFormVisible = value;
      }),
    setSortedExperienceUserIds: (ids: any[]) =>
      set((state) => {
        state.sortedExperienceUserIds = ids;
      }),
    setIsOpenExperienceUserGradingModal: (value: boolean) =>
      set((state) => {
        state.isOpenExperienceUserGradingModal = value;
      }),
    setIsOpenOverallGradingModal: (value: boolean) =>
      set((state) => {
        state.isOpenOverallGradingModal = value;
      }),
    setLocalExperienceUser: (payload: any) =>
      set((state) => {
        const { experience_id, experience_user_id, value } = payload;
        console.log('setExperienceUsers ==>  payload', payload);
        const experienceUserIdentifier = `eId_${experience_id}_euId_${experience_user_id}`;
        state.experience_users[experienceUserIdentifier] = value;
      }),
    setIsGradeSavingInProgress: (value: boolean) =>
      set((state) => {
        state.isGradeSavingInProgress = value;
      }),
    setAnnotations: (annotations: any[] | undefined, options = { merge: true }) =>
      set((state) => {
        const incomingAnnotations = annotations ?? [];
        const merge = options?.merge ?? true;
        if (merge) {
          const existingAnnotations = state.annotations ?? [];
          const getAnnotationId = (annotation: any) => annotation?.id ?? annotation?.attributes?.id;

          const incomingAnnotationIds = new Set(
            incomingAnnotations
              .map((annotation: any) => getAnnotationId(annotation))
              .filter(Boolean)
          );

          const missingAnnotations = existingAnnotations.filter((annotation: any) => {
            const annotationId = getAnnotationId(annotation);
            return annotationId && !incomingAnnotationIds.has(annotationId);
          });

          state.annotations = [...incomingAnnotations, ...missingAnnotations];
        } else {
          state.annotations = incomingAnnotations;
        }
      }),
    reset: () => {
      set(() => ({ ...initialState }));
    },
    setAssignedReviewersToStore: (experienceUserId: number, reviewerUserIds: number[]) =>
      set((state) => {
        state.assignedReviewersFromStore[experienceUserId] = reviewerUserIds;
      }),
    clearAssignedReviewersFromStore: () =>
      set((state) => {
        state.assignedReviewersFromStore = {};
      }),
  }))
);

// Individual action selectors for better performance
export const selectSetStudentNameFormat = (state: ExperienceUserStore) =>
  state.setStudentNameFormat;
export const selectSetAIGradingInProgressForEUIds = (state: ExperienceUserStore) =>
  state.setAIGradingInProgressForEUIds;
export const selectSetActiveExperienceUserId = (state: ExperienceUserStore) =>
  state.setActiveExperienceUserId;
export const selectSetOverallGradingFormVisible = (state: ExperienceUserStore) =>
  state.setOverallGradingFormVisible;
export const selectSetSortedExperienceUserIds = (state: ExperienceUserStore) =>
  state.setSortedExperienceUserIds;
export const selectSetIsOpenExperienceUserGradingModal = (state: ExperienceUserStore) =>
  state.setIsOpenExperienceUserGradingModal;
export const selectSetIsOpenOverallGradingModal = (state: ExperienceUserStore) =>
  state.setIsOpenOverallGradingModal;
export const selectSetLocalExperienceUser = (state: ExperienceUserStore) =>
  state.setLocalExperienceUser;
export const selectSetIsGradeSavingInProgress = (state: ExperienceUserStore) =>
  state.setIsGradeSavingInProgress;
// State selectors
export const selectActiveExperienceUserId = (state: ExperienceUserStore): number | null =>
  state.activeExperienceUserId;

export const selectActiveExperienceUserExperienceId = (state: ExperienceUserStore): number | null =>
  state.activeExperienceUserExperienceId;

export const selectAIGradingInProgressForEUIds = (state: ExperienceUserStore): number[] =>
  state.aiGradingInProgressForEUIds;

export const selectStudentNameFormat = (state: ExperienceUserStore): 'first_name' | 'last_name' =>
  state.studentNameFormat;

export const selectOverallGradingFormVisible = (state: ExperienceUserStore): boolean =>
  state.overallGradingFormVisible;

export const selectSortedExperienceUserIds = (state: ExperienceUserStore): any[] =>
  state.sortedExperienceUserIds;

export const selectIsOpenExperienceUserGradingModal = (state: ExperienceUserStore): boolean =>
  state.isOpenExperienceUserGradingModal;

export const selectIsOpenOverallGradingModal = (state: ExperienceUserStore): boolean =>
  state.isOpenOverallGradingModal;

export const selectExperienceUser = (state: ExperienceUserStore, options: any) => {
  const experienceUserIdentifier = `eId_${options.experienceId}_euId_${options.experienceUserId}`;
  return state.experience_users[experienceUserIdentifier] ?? null;
};

export const selectIsGradeSavingInProgress = (state: ExperienceUserStore): boolean =>
  state.isGradeSavingInProgress;

export const selectAnnotations = (state: ExperienceUserStore): any[] => state.annotations;

/**
 * Returns annotations scoped to a given experience user.
 * Usage: useExperienceUserStore(selectAnnotationsByFilter(options)))
 */
export const selectAnnotationsByFilter =
  (options: any) =>
  (state: ExperienceUserStore): any[] => {
    const list = state.annotations ?? [];
    if (options == null || options === '') {
      return list;
    }
    if (options.userId) {
      return list.filter((annotation) => {
        return (
          annotation.attributes?.user_id === options.userId &&
          annotation.attributes?.archived !== true
        );
      });
    }
    return list;
  };

export const selectAssignedReviewersFromStore = (
  state: ExperienceUserStore
): Record<number, number[]> => state.assignedReviewersFromStore;

export const selectSetAssignedReviewersToStore = (state: ExperienceUserStore) =>
  state.setAssignedReviewersToStore;

export const selectClearAssignedReviewersFromStore = (state: ExperienceUserStore) =>
  state.clearAssignedReviewersFromStore;
